import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import { useCallback, useEffect, useState, useContext } from 'react';
import { vexCdnUrl } from '~/constants/environment';
import { InventoryTypeKey } from '~/constants/inventoryType';
import { useIsLoggedIn } from '~/context/common/UserContext';
import { useToggleFavorite } from '~/hooks/common/favorites';
import HeartWithBorder from '~/icons/HeartWithBorder';
import {
  FavoriteWrapper,
  Tile,
  TileImageSection,
  TileInfoSection,
  Trim,
  TrimMileage,
  YearMakeModel
} from './VehicleDetailsTile.styles';
import abbreviateNumber from '../utils/abbreviateNumber';

type VehicleProps = {
  id: number;
  imageUrl: string;
  make: string;
  model: string;
  mileage?: number;
  price?: number;
  sectionTitle?: string;
  trim: string;
  year?: number;
  stockNumber: number;
  isFavorited: boolean;
  vehicleInventoryType: InventoryTypeKey;
};

export default function VehicleDetailsTile({
  id: vehicleId,
  isFavorited,
  make,
  model,
  mileage = 0,
  price = 0,
  sectionTitle,
  trim,
  year = 0,
  stockNumber,
  vehicleInventoryType,
  imageUrl: imgUrl
}: VehicleProps) {
  const [imgError, setImgError] = useState(false);
  const imageUrl =
    vehicleInventoryType === InventoryTypeKey.WIP
      ? imgUrl
      : `${vexCdnUrl}/${stockNumber}/standardizedHero.jpg`;
  const toggleFavorite = useToggleFavorite();
  const isLoggedIn = useIsLoggedIn();
  const [isFavoritedLocally, setFavoritedLocally] = useState(isFavorited);

  const { trackEvent } = useContext(AnalyticsContext);

  const handleFavorite = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const customAttributes = {
        description: `${sectionTitle} heart cta`,
        vehicleId
      };

      toggleFavorite({ vehicleId });
      if (isLoggedIn) setFavoritedLocally(v => !v);
      trackEvent('Clicked - Home feed card', customAttributes);
    },
    [isLoggedIn, sectionTitle, toggleFavorite, trackEvent, vehicleId]
  );

  useEffect(() => {
    setFavoritedLocally(isFavorited);
  }, [isFavorited]);

  return (
    <Tile>
      <TileImageSection>
        <Clickable
          data-analytics-id="Home feed card"
          element={FavoriteWrapper}
          onClick={handleFavorite}
        >
          <HeartWithBorder
            blue
            height={20}
            width={20}
            isFavorited={isFavoritedLocally}
            data-qa={'recently-viewed-favorite-button'}
          />
        </Clickable>
        <img
          className="rounded-t-md object-cover"
          src={
            imgError
              ? `${process.env.CVNA_APP_VEX_STOCK_CDN_URL}/generic_vehicle_desktop_1920x1080.png`
              : imageUrl
          }
          alt={`${make} ${model}`}
          onError={() => setImgError(true)}
        />
      </TileImageSection>
      <TileInfoSection>
        <YearMakeModel>
          {year} {make} {model}
        </YearMakeModel>
        <TrimMileage>
          <Trim>{trim} |</Trim>
          <span>
            &nbsp;
            {abbreviateNumber(mileage)} mi
          </span>
        </TrimMileage>
        <h3 className="tl-header-m">${`${price?.toLocaleString()}`}</h3>
      </TileInfoSection>
    </Tile>
  );
}
